import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useModels } from "@/components/chat/Composer";
import { toggleOption, toolOptions, useAvailableServers } from "@/components/chat/ToolsPicker";
import { Badge, Button, Card, Checkbox, ErrorText, Input, Label, Modal, Select, Textarea } from "@/components/ui";
import { api } from "@/lib/api";
import { useMe } from "@/lib/auth";
import { useFolders } from "@/lib/folders";
import { useTemplates, type ChatTemplate, type TemplateInput } from "@/lib/templates";

const EMPTY: TemplateInput = {
  name: "",
  description: "",
  model_id: null,
  mcp_servers: null,
  system_prompt: "",
  first_message: "",
  temporary: false,
  folder_id: null,
  is_global: false,
};

export default function TemplateSettings() {
  const templates = useTemplates();
  const models = useModels();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<ChatTemplate | "new" | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["templates"] });
  const remove = useMutation({ mutationFn: (id: string) => api.delete(`/api/templates/${id}`), onSuccess: refresh });
  const modelName = (id: string | null) => models.data?.find((m) => m.id === id)?.name;

  return (
    <Card
      title="Chat templates"
      actions={
        <Button size="sm" onClick={() => setEditing("new")}>
          <Plus size={14} /> New template
        </Button>
      }
    >
      <p className="mb-3 text-sm text-zinc-500">
        A template is a starting point for new chats: model, tools, system prompt, and optionally a work folder and a first message. Start one from
        "From template" above the chat list, or save a chat's current settings as a template with the button in its header.
      </p>
      <ErrorText error={templates.error || remove.error} />
      {templates.data && !templates.data.length && <p className="text-sm text-zinc-500">No templates yet.</p>}
      <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
        {templates.data?.map((t) => (
          <li key={t.id} className="flex items-center gap-3 py-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate font-medium">{t.name}</span>
                {t.is_global && <Badge tone="indigo">for everyone</Badge>}
              </div>
              <p className="truncate text-xs text-zinc-500">
                {[t.description, modelName(t.model_id), t.system_prompt && "system prompt", t.first_message && "first message", t.temporary && "temporary"].filter(Boolean).join(" · ")}
              </p>
            </div>
            {t.editable && (
              <>
                <Button variant="ghost" size="icon" aria-label={`Edit ${t.name}`} onClick={() => setEditing(t)}>
                  <Pencil size={16} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Delete ${t.name}`}
                  onClick={() => window.confirm(`Delete the template "${t.name}"?`) && remove.mutate(t.id)}
                >
                  <Trash2 size={16} />
                </Button>
              </>
            )}
          </li>
        ))}
      </ul>
      {editing && <TemplateForm template={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={refresh} />}
    </Card>
  );
}

function TemplateForm({ template, onClose, onSaved }: { template: ChatTemplate | null; onClose: () => void; onSaved: () => void }) {
  const me = useMe().data;
  const models = useModels().data ?? [];
  const folders = useFolders().data ?? [];
  const servers = useAvailableServers().data ?? [];
  const [form, setForm] = useState<TemplateInput>(
    template ? { ...EMPTY, ...template, system_prompt: template.system_prompt ?? "", first_message: template.first_message ?? "" } : EMPTY,
  );
  const save = useMutation({
    mutationFn: () => (template ? api.put(`/api/templates/${template.id}`, form) : api.post("/api/templates", form)),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  const options = toolOptions(servers);
  const on = form.mcp_servers ?? servers.filter((s) => s.default_on).map((s) => s.id);
  const isAdmin = me?.role === "admin";
  return (
    <Modal open onClose={onClose} title={template ? "Edit template" : "New template"} wide>
      <form
        className="space-y-4"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <Label label="Name">
          <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Label>
        <Label label="Description">
          <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Label>
        <Label label="Model">
          <Select value={form.model_id ?? ""} onChange={(e) => setForm({ ...form, model_id: e.target.value || null })}>
            <option value="">Your usual default</option>
            {models.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </Select>
        </Label>
        <div className="space-y-1.5">
          <span className="text-sm font-medium">Tools</span>
          <Checkbox
            label="Use the default tools"
            checked={form.mcp_servers === null}
            onChange={(e) => setForm({ ...form, mcp_servers: e.target.checked ? null : on })}
          />
          {form.mcp_servers !== null && (
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {options.map((o) => (
                <Checkbox
                  key={o.key}
                  label={o.label}
                  checked={o.ids.every((id) => on.includes(id))}
                  onChange={() => setForm({ ...form, mcp_servers: toggleOption(on, o) })}
                />
              ))}
              {!options.length && <p className="text-sm text-zinc-500">No tools are available.</p>}
            </div>
          )}
        </div>
        <Label label="System prompt">
          <Textarea rows={5} value={form.system_prompt ?? ""} onChange={(e) => setForm({ ...form, system_prompt: e.target.value })} />
        </Label>
        <Label label="First message" hint="Put in the message box when the template is used; you can edit it before sending.">
          <Textarea rows={3} value={form.first_message ?? ""} onChange={(e) => setForm({ ...form, first_message: e.target.value })} />
        </Label>
        {!form.is_global && folders.length > 0 && (
          <Label label="Work folder">
            <Select value={form.folder_id ?? ""} onChange={(e) => setForm({ ...form, folder_id: e.target.value || null })}>
              <option value="">No folder</option>
              {folders.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </Select>
          </Label>
        )}
        <Checkbox
          label="Start as a temporary chat (you can still switch it off before sending)"
          checked={form.temporary}
          onChange={(e) => setForm({ ...form, temporary: e.target.checked })}
        />
        {isAdmin && !template && (
          <Checkbox
            label="Make this template available to everyone (cannot use a work folder)"
            checked={form.is_global}
            onChange={(e) => setForm({ ...form, is_global: e.target.checked, folder_id: e.target.checked ? null : form.folder_id })}
          />
        )}
        <ErrorText error={save.error} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={save.isPending}>Save</Button>
        </div>
      </form>
    </Modal>
  );
}
